<template>
    <div class="print_direction_block">
        <p v-if="currentArtSize">
            Imprint Area: W {{ currentArtSize.width }}" X H
            {{ currentArtSize.height }}"
            <span v-if="normalizedSelectedArtSize">
                | Art Size: H {{ normalizedSelectedArtSize.height }}" X W
                {{ normalizedSelectedArtSize.width }}"
            </span>
        </p>
        <p v-else>Imprint Area: -</p>
        <div class="print_direction_main">
            <div
                v-for="(print_area, index) in filteredPrintAreas"
                :key="index"
                :class="
                    print_area.label.toLowerCase() == current_flip
                        ? 'print-area-tab active'
                        : 'print-area-tab'
                "
            >
                <button
                    :id="print_area.label + '_flipbtn'"
                    type="button"
                    class="print-area-tab-button"
                    :title="print_area.label + ' View'"
                    @click="changeImageView(print_area.label, index)"
                    :data-original-title="print_area.label + ' View'"
                >
                    <img
                        name="tshirtview"
                        class="print-area-icon"
                        :src="print_area.short_thumbnail_image"
                        alt=""
                    />
                    <span class="print-area-label">{{
                        print_area.label_show || print_area.label
                    }}</span>
                </button>
                <button
                    type="button"
                    class="print-area-add-btn"
                    :class="{
                        'active-area': print_area.selected === true
                    }"
                    :title="
                        print_area.selected === true
                            ? (print_area.label_show || print_area.label) +
                              ' Selected'
                            : 'Add ' +
                              (print_area.label_show || print_area.label)
                    "
                    @click="toggleAreaSelection(print_area, index)"
                >
                    <!-- Green Tick Icon for Selected Area (selected === true) -->
                    <svg
                        v-if="print_area.selected === true"
                        class="tick-icon"
                        viewBox="0 0 24 24"
                        fill="none"
                        xmlns="http://www.w3.org/2000/svg"
                    >
                        <circle cx="12" cy="12" r="10" fill="#22c55e" />
                        <path
                            d="M8 12L11 15L16 9"
                            stroke="white"
                            stroke-width="2.5"
                            stroke-linecap="round"
                            stroke-linejoin="round"
                        />
                    </svg>
                    <!-- Plus Icon for Non-Selected Areas -->
                    <svg
                        v-else
                        class="add-icon"
                        viewBox="0 0 24 24"
                        fill="none"
                        xmlns="http://www.w3.org/2000/svg"
                    >
                        <path
                            d="M12 5V19M5 12H19"
                            stroke="currentColor"
                            stroke-width="2"
                            stroke-linecap="round"
                        />
                    </svg>
                </button>
            </div>
            <!-- ALL button: show when more than 2 design areas selected (view-only preview) -->
            <div
                v-if="showAllButton"
                :class="
                    current_flip === 'all'
                        ? 'print-area-tab active'
                        : 'print-area-tab'
                "
            >
                <button
                    type="button"
                    class="print-area-tab-button print-area-all-btn"
                    title="View all design areas"
                    @click="onViewAllClick"
                >
                    <span class="print-area-label">Preview</span>
                </button>
            </div>
        </div>
    </div>
</template>

<script>
export default {
    name: "PrintAreaIcons",
    props: {
        product_type_id: {
            type: [String, Number],
            default: () => localStorage.getItem("product_type_id") || null
        },
        front_show: {
            type: Boolean,
            default: () => true
        },
        back_show: {
            type: Boolean,
            default: () => true
        },
        left_show: {
            type: Boolean,
            default: () => true
        },
        right_show: {
            type: Boolean,
            default: () => true
        },
        product_design_area: {
            type: [Array, Object],
            default: () => []
        },
        current_flip: {
            type: String,
            default: () => localStorage.getItem("active_flip")
        },
        gender_groups: {
            type: Array,
            default: () => []
        },
        disabled: {
            type: Boolean,
            default: false
        },
        colorsValidation: {
            type: String,
            default: ""
        },
        selectedArtSize: {
            type: Object,
            default: null
        }
    },
    data() {
        return {
            isLoader: false,
            print_areas: []
        };
    },
    components: {},
    computed: {
        filteredPrintAreas() {
            // Show all available areas - no filtering based on show props
            // All areas from imprint_locations should always be visible
            if (!this.print_areas || this.print_areas.length === 0) {
                return [];
            }
            // Return all areas - they should always be visible
            // selected state only determines green tick vs plus icon
            return this.print_areas;
        },
        showAllButton() {
            return this.selectedDesignAreaCount > 0;
        },
        selectedDesignAreaCount() {
            if (!this.print_areas || !this.print_areas.length) return 0;
            return this.print_areas.filter(
                (area) => area && area.selected === true
            ).length;
        },
        currentArtSize() {
            // Find the current active print area based on current_flip
            if (
                !this.current_flip ||
                !this.print_areas ||
                this.print_areas.length === 0
            ) {
                return null;
            }

            // Find the print area that matches current_flip
            const currentArea = this.print_areas.find((area) => {
                if (!area || !area.label) return false;
                return (
                    area.label.toLowerCase() === this.current_flip.toLowerCase()
                );
            });

            if (!currentArea) {
                return null;
            }

            // Extract widthIn and heightIn from the current area
            const widthIn = currentArea.widthIn;
            const heightIn = currentArea.heightIn;

            // Return null if dimensions are not available
            if (
                widthIn === undefined ||
                widthIn === null ||
                heightIn === undefined ||
                heightIn === null
            ) {
                return null;
            }

            // Format to 2 decimal places
            return {
                width: parseFloat(widthIn).toFixed(2),
                height: parseFloat(heightIn).toFixed(2)
            };
        },
        normalizedSelectedArtSize() {
            if (
                !this.selectedArtSize ||
                typeof this.selectedArtSize !== "object"
            ) {
                return null;
            }

            const width = parseFloat(this.selectedArtSize.width);
            const height = parseFloat(this.selectedArtSize.height);

            if (
                !Number.isFinite(width) ||
                width <= 0 ||
                !Number.isFinite(height) ||
                height <= 0
            ) {
                return null;
            }

            return {
                width: width.toFixed(2),
                height: height.toFixed(2)
            };
        }
    },
    watch: {
        product_design_area: {
            handler(newVal) {
                if (
                    newVal &&
                    (Array.isArray(newVal) || typeof newVal === "object")
                ) {
                    this.getPrintArea();
                }
            },
            immediate: true,
            deep: true
        },
        gender_groups: {
            handler(newVal, oldVal) {
                // Always refresh selected state when gender_groups changes
                // This ensures green tick updates when setNewDesign sets is_selected
                if (!newVal || !Array.isArray(newVal) || newVal.length === 0) {
                    return;
                }

                const newGroup = newVal[0];
                if (
                    !newGroup ||
                    !newGroup.imprint_locations ||
                    !Array.isArray(newGroup.imprint_locations)
                ) {
                    return;
                }

                // If this is the first time (no oldVal), do full load
                if (!oldVal || !oldVal[0] || !oldVal[0].imprint_locations) {
                    this.getPrintArea();
                    return;
                }

                // Check if imprint_locations structure changed
                const oldGroup = oldVal[0];
                if (
                    !oldGroup.imprint_locations ||
                    oldGroup.imprint_locations.length !==
                        newGroup.imprint_locations.length
                ) {
                    this.getPrintArea();
                    return;
                }

                // Check if any location's is_selected changed
                // This is important for setNewDesign to update green tick
                let isSelectedChanged = false;
                for (let i = 0; i < newGroup.imprint_locations.length; i++) {
                    const newLoc = newGroup.imprint_locations[i];
                    const oldLoc = oldGroup.imprint_locations[i];

                    if (oldLoc && newLoc.is_selected !== oldLoc.is_selected) {
                        isSelectedChanged = true;
                        break;
                    }
                }

                // If is_selected changed, refresh selected state
                // Otherwise, only refresh if structure changed
                if (isSelectedChanged) {
                    this.$nextTick(() => {
                        this.refreshSelectedState();
                    });
                }
            },
            immediate: true,
            deep: true
        },
        current_flip: {
            handler() {
                // Force reactivity update when current_flip changes
                this.$forceUpdate();
            },
            immediate: true
        }
    },
    mounted() {
        this.getPrintArea();
    },
    methods: {
        getPrintArea() {
            let _this = this;

            // Get all available imprint_locations from gender_groups (like ItemDetails does)
            // This ensures we show ALL available areas (Front, Back, Left, Right)
            let allImprintLocations = [];
            if (
                this.gender_groups &&
                Array.isArray(this.gender_groups) &&
                this.gender_groups.length > 0
            ) {
                // Use first gender group (index 0) to get all available imprint locations
                const genderGroup = this.gender_groups[0];
                if (
                    genderGroup &&
                    genderGroup.imprint_locations &&
                    Array.isArray(genderGroup.imprint_locations)
                ) {
                    allImprintLocations = genderGroup.imprint_locations;
                }
            }

            // Get product design areas for area details (dimensions, images, etc.)
            let productAreasMap = {};
            if (this.product_design_area) {
                let productAreasArray = [];
                if (Array.isArray(this.product_design_area)) {
                    productAreasArray = this.product_design_area;
                } else if (
                    typeof this.product_design_area === "object" &&
                    this.product_design_area !== null
                ) {
                    productAreasArray = Object.values(this.product_design_area);
                }

                // Create a map for quick lookup by label (case-insensitive)
                productAreasArray.forEach((area) => {
                    if (area && area.label) {
                        const labelKey = area.label.toLowerCase().trim();
                        productAreasMap[labelKey] = area;

                        // Also map by label_show if different
                        if (area.label_show && area.label_show !== area.label) {
                            const labelShowKey = area.label_show
                                .toLowerCase()
                                .trim();
                            if (!productAreasMap[labelShowKey]) {
                                productAreasMap[labelShowKey] = area;
                            }
                        }
                    }
                });
            }

            // If we have imprint_locations, use them to show all available areas
            if (allImprintLocations.length > 0) {
                // Map imprint_locations to print areas, merging with product_design_area data
                _this.print_areas = allImprintLocations
                    .map((imprintLoc) => {
                        if (!imprintLoc || !imprintLoc.location) {
                            return null;
                        }

                        // Find matching product area by label (case-insensitive, handle variations)
                        const locationLabel = imprintLoc.location
                            .toLowerCase()
                            .trim();

                        // Try exact match first
                        let matchingProductArea =
                            productAreasMap[locationLabel];

                        // If no exact match, try partial matching (e.g., "left sleeve" matches "left")
                        if (!matchingProductArea) {
                            const locationWords = locationLabel.split(/\s+/);
                            if (locationWords.length > 1) {
                                // Try matching with first word (e.g., "left" from "left sleeve")
                                matchingProductArea =
                                    productAreasMap[locationWords[0]];
                            }

                            // Also try reverse - check if any product area label contains location
                            if (!matchingProductArea) {
                                for (const [key, area] of Object.entries(
                                    productAreasMap
                                )) {
                                    if (
                                        key.includes(locationLabel) ||
                                        locationLabel.includes(key)
                                    ) {
                                        matchingProductArea = area;
                                        break;
                                    }
                                }
                            }
                        }

                        // Create print area object
                        const printArea = {
                            label:
                                (matchingProductArea &&
                                    matchingProductArea.label) ||
                                imprintLoc.location ||
                                "",
                            label_show:
                                (matchingProductArea &&
                                    matchingProductArea.label_show) ||
                                imprintLoc.location ||
                                "",
                            // Use is_selected from imprint_locations to determine selected state
                            selected: imprintLoc.is_selected === true,
                            // Get area details from product_design_area if available
                            widthIn:
                                (matchingProductArea &&
                                    matchingProductArea.widthIn) ||
                                null,
                            heightIn:
                                (matchingProductArea &&
                                    matchingProductArea.heightIn) ||
                                null,
                            short_thumbnail_image:
                                (matchingProductArea &&
                                    matchingProductArea.short_thumbnail_image) ||
                                (matchingProductArea &&
                                    matchingProductArea.thumbnail) ||
                                (imprintLoc && imprintLoc.image_path) ||
                                "",
                            thumbnail:
                                (matchingProductArea &&
                                    matchingProductArea.thumbnail) ||
                                (matchingProductArea &&
                                    matchingProductArea.short_thumbnail_image) ||
                                (imprintLoc && imprintLoc.image_path) ||
                                ""
                        };

                        // Merge any additional properties from matching product area
                        // But preserve selected state from imprint_locations (don't override)
                        if (matchingProductArea) {
                            Object.keys(matchingProductArea).forEach((key) => {
                                // Don't override selected - it comes from imprint_locations.is_selected
                                if (
                                    key !== "selected" &&
                                    !printArea.hasOwnProperty(key)
                                ) {
                                    printArea[key] = matchingProductArea[key];
                                }
                            });
                        }

                        return printArea;
                    })
                    .filter((area) => area !== null && area.label);
            } else {
                // Fallback to product_design_area if no imprint_locations available
                if (
                    !this.product_design_area ||
                    (Array.isArray(this.product_design_area) &&
                        this.product_design_area.length === 0) ||
                    (typeof this.product_design_area === "object" &&
                        Object.keys(this.product_design_area).length === 0)
                ) {
                    _this.print_areas = [];
                    _this.isLoader = false;
                    return;
                }

                // Process product_design_area from JSON
                // Show all available areas - don't filter based on selected state
                if (Array.isArray(this.product_design_area)) {
                    _this.print_areas = this.product_design_area.filter(
                        (area) => area && area.label
                    );
                } else if (
                    typeof this.product_design_area === "object" &&
                    this.product_design_area !== null
                ) {
                    // Convert object to array
                    _this.print_areas = Object.values(
                        this.product_design_area
                    ).filter((area) => area && area.label);
                } else {
                    _this.print_areas = [];
                    _this.isLoader = false;
                    return;
                }
            }

            // Ensure all required fields are present - use label_show from design area JSON
            _this.print_areas = _this.print_areas
                .map((area) => {
                    if (!area) return null;
                    return {
                        ...area,
                        label: area.label || "",
                        // Use label_show from design area JSON, fallback to label
                        label_show: area.label_show || area.label || "",
                        // Use short_thumbnail_image, fallback to thumbnail if available
                        short_thumbnail_image:
                            area.short_thumbnail_image || area.thumbnail || "",
                        thumbnail:
                            area.thumbnail || area.short_thumbnail_image || "",
                        // Preserve selected state - true means selected, false/undefined means not selected
                        selected: area.selected === true
                    };
                })
                .filter((area) => area !== null && area.label); // Filter out areas without label

            // Trigger function only if we have print areas
            if (_this.print_areas.length > 0) {
                _this.$emit("getPrintAreaParent", _this.print_areas);

                // Skip changePrintArea if flag is set (prevents view switch back to front)
                if (_this.print_areas[0] && _this.print_areas[0].label) {
                    let parent = _this.$parent;
                    let foundParent = null;
                    while (parent) {
                        if (parent && typeof parent.updatePrintAreas === 'function') {
                            foundParent = parent;
                            break;
                        }
                        parent = parent.$parent;
                    }
                    
                    if (!foundParent || !foundParent._fromPlusIconClick) {
                        _this.$emit("changePrintArea", _this.print_areas[0].label);
                    }
                }

                // Only call changeImageView if current_flip exists and we have valid areas
                if (_this.current_flip && _this.print_areas.length > 0) {
                    // Use $nextTick to ensure DOM is ready before calling changeImageView
                    this.$nextTick(() => {
                        // Find matching print area for current_flip
                        let matchingArea = _this.print_areas.find(
                            (area) =>
                                area &&
                                area.label &&
                                area.label.toLowerCase() ===
                                    _this.current_flip.toLowerCase()
                        );
                        if (matchingArea && matchingArea.label) {
                            this.changeImageView(matchingArea.label, 0);
                        }
                    });
                }
            }
            _this.isLoader = false;
        },
        refreshSelectedState() {
            // Refresh selected state from imprint_locations without full reload
            // This is called when is_selected changes (e.g., from setNewDesign)
            if (
                !this.gender_groups ||
                !Array.isArray(this.gender_groups) ||
                this.gender_groups.length === 0
            ) {
                return;
            }

            const genderGroup = this.gender_groups[0];
            if (
                !genderGroup ||
                !genderGroup.imprint_locations ||
                !Array.isArray(genderGroup.imprint_locations)
            ) {
                return;
            }

            // If print_areas is not initialized, do full load first
            if (!this.print_areas || this.print_areas.length === 0) {
                this.getPrintArea();
                return;
            }

            // Update selected state for each print area based on imprint_locations
            this.print_areas.forEach((printArea) => {
                if (!printArea || !printArea.label) return;

                const matchingLocation = genderGroup.imprint_locations.find(
                    (loc) => {
                        if (!loc || !loc.location) return false;
                        const locLabel = loc.location.toLowerCase().trim();
                        const areaLabel = printArea.label.toLowerCase().trim();
                        
                        // Exact match
                        if (locLabel === areaLabel) {
                            return true;
                        }
                        
                        // If either label contains "left", match with "left"
                        if (locLabel.includes("left") && areaLabel.includes("left")) {
                            return true;
                        }
                        
                        // If either label contains "right", match with "right"
                        if (locLabel.includes("right") && areaLabel.includes("right")) {
                            return true;
                        }
                        
                        // If either label contains "front", match with "front"
                        if (locLabel.includes("front") && areaLabel.includes("front")) {
                            return true;
                        }
                        
                        // If either label contains "back", match with "back"
                        if (locLabel.includes("back") && areaLabel.includes("back")) {
                            return true;
                        }
                        
                        return false;
                    }
                );

                if (matchingLocation) {
                    const newSelectedState =
                        matchingLocation.is_selected === true;
                    // Always update, even if same value, to ensure reactivity
                    this.$set(printArea, "selected", newSelectedState);
                }
            });

            // Force update to ensure UI reflects the changes
            this.$forceUpdate();
        },
        onViewAllClick() {
            if (this.disabled) {
                if (this.$toast) {
                    this.$toast.error(this.colorsValidation, {
                        position: "top-right",
                        queue: true
                    });
                }
                return;
            }
            this.$emit("viewAllDesignAreas");
        },
        changeImageView(printArea, index) {
            if (!printArea) {
                return;
            }

            if (this.disabled) {
                if (this.$toast) {
                    this.$toast.error(this.colorsValidation, {
                        position: "top-right",
                        queue: true
                    });
                }
                return;
            }

            let print_area = "front";
            if (printArea == "Front") {
                print_area = "front";
            } else if (printArea == "Back") {
                print_area = "back";
            } else if (["Left", "Left Sleeve"].includes(printArea)) {
                print_area = "left";
            } else if (["Right", "Right Sleeve"].includes(printArea)) {
                print_area = "right";
            }
            
            // Auto-select the view if not already selected (like plus icon behavior)
            // Find the matching print area from print_areas array
            const matchingPrintArea = this.print_areas.find((area) => {
                if (!area || !area.label) return false;
                const areaLabel = (area.label || "").toLowerCase().trim();
                if (print_area === "left" && areaLabel.includes("left")) return true;
                if (print_area === "right" && areaLabel.includes("right")) return true;
                if (print_area === "front" && areaLabel.includes("front")) return true;
                if (print_area === "back" && areaLabel.includes("back")) return true;
                return false;
            });
            
            // If view is not selected, programmatically trigger selection first (like double click)
            if (matchingPrintArea && !matchingPrintArea.selected) {
                // Programmatically trigger toggleAreaSelection (like plus icon click)
                this.toggleAreaSelection(matchingPrintArea, index);
                
                // Then trigger view change after a small delay to ensure selection completes
                setTimeout(() => {
                    // Trigger function
                    this.$emit("flipTshirt", print_area);
                    // Add Active Class
                    this.$emit("changePrintArea", print_area);
                }, 50);
            } else {
                // View is already selected, just change view
                // Trigger function
                this.$emit("flipTshirt", print_area);
                // Add Active Class
                this.$emit("changePrintArea", print_area);
            }
        },
        toggleAreaSelection(print_area, index) {
            if (this.disabled) {
                if (this.$toast) {
                    this.$toast.error(this.colorsValidation, {
                        position: "top-right",
                        queue: true
                    });
                }
                return;
            }

            // Find the matching imprint_location in gender_groups
            if (
                !this.gender_groups ||
                !Array.isArray(this.gender_groups) ||
                this.gender_groups.length === 0
            ) {
                return;
            }

            const genderGroup = this.gender_groups[0];
            if (
                !genderGroup ||
                !genderGroup.imprint_locations ||
                !Array.isArray(genderGroup.imprint_locations)
            ) {
                return;
            }

            // Find the matching imprint_location by label
            const matchingLocationIndex =
                genderGroup.imprint_locations.findIndex((loc) => {
                    if (!loc || !loc.location) return false;
                    const locLabel = loc.location.toLowerCase().trim();
                    const areaLabel = print_area.label.toLowerCase().trim();
                    
                    // Exact match
                    if (locLabel === areaLabel) {
                        return true;
                    }
                    
                    // If either label contains "left", match with "left"
                    if (locLabel.includes("left") && areaLabel.includes("left")) {
                        return true;
                    }
                    
                    // If either label contains "right", match with "right"
                    if (locLabel.includes("right") && areaLabel.includes("right")) {
                        return true;
                    }
                    
                    // If either label contains "front", match with "front"
                    if (locLabel.includes("front") && areaLabel.includes("front")) {
                        return true;
                    }
                    
                    // If either label contains "back", match with "back"
                    if (locLabel.includes("back") && areaLabel.includes("back")) {
                        return true;
                    }
                    
                    return false;
                });

            if (matchingLocationIndex === -1) {
                return;
            }

            const imprintLocation =
                genderGroup.imprint_locations[matchingLocationIndex];

            // Check if at least one location must remain selected (like ItemDetails does)
            let no_of_location = 0;
            let previous_index = "";
            genderGroup.imprint_locations.forEach((location, key) => {
                if (location.is_selected) {
                    no_of_location++;
                    previous_index = key;
                }
            });

            // Maintain only one selection in imprint locations (prevent deselecting if only one is selected)
            if (
                no_of_location == 1 &&
                previous_index == matchingLocationIndex
            ) {
                return true;
            }

            // Get the new selection state before toggling
            const newSelectionState = !imprintLocation.is_selected;

            // Only update is_selected flag - don't clear design data
            // Design data should persist when toggling selection
            this.$set(imprintLocation, "is_selected", newSelectionState);

            // Update all colors' imprint_locations arrays - only update is_selected flag
            // Don't clear colors/design data as it will remove designs from canvas
            if (genderGroup.colors && Array.isArray(genderGroup.colors)) {
                genderGroup.colors.forEach((color) => {
                    if (
                        color.imprint_locations &&
                        Array.isArray(color.imprint_locations) &&
                        color.imprint_locations[matchingLocationIndex]
                    ) {
                        // Only update is_selected flag, preserve design data
                        this.$set(
                            color.imprint_locations[matchingLocationIndex],
                            "is_selected",
                            newSelectionState
                        );
                    }
                });
            }

            // Update local print_area state
            this.$set(print_area, "selected", newSelectionState);

            // Find parent component that has updatePrintAreas method
            // Traverse up the component tree to find DesignStudioV3
            let parent = this.$parent;
            let foundParent = null;
            while (parent) {
                if (parent && typeof parent.updatePrintAreas === 'function') {
                    foundParent = parent;
                    break;
                }
                parent = parent.$parent;
            }

            // This ensures design on current area is preserved when toggling
            if (foundParent && foundParent.backCanvas && foundParent.active_flip) {
                try {
                    let new_canvas_elements = localStorage.getItem("new_canvas_elements");
                    let parsedJson = new_canvas_elements
                        ? JSON.parse(new_canvas_elements)
                        : { front: "", back: "", left: "", right: "" };
                    
                    // Save current active flip canvas data
                    parsedJson[foundParent.active_flip] = JSON.stringify(foundParent.backCanvas);
                    localStorage.setItem(
                        "new_canvas_elements",
                        JSON.stringify(parsedJson)
                    );
                    
                    // Also save canvas colors
                    let get_canvas_colors = JSON.parse(
                        localStorage.getItem("canvas_design_colors") || "{}"
                    );
                    if (foundParent.design_current_colors) {
                        get_canvas_colors[foundParent.active_flip] = foundParent.design_current_colors;
                        localStorage.setItem(
                            "canvas_design_colors",
                            JSON.stringify(get_canvas_colors)
                        );
                    }
                } catch (e) {
                    console.warn("Error saving canvas data:", e);
                }
            }

            if (foundParent) {
                // Plus icon click - change view to selected area
                if (newSelectionState === true && print_area && print_area.label) {
                    let viewType = print_area.label.toLowerCase().trim();
                    if (viewType.includes("left")) {
                        viewType = "left";
                    } else if (viewType.includes("right")) {
                        viewType = "right";
                    } else if (viewType.includes("front")) {
                        viewType = "front";
                    } else if (viewType.includes("back")) {
                        viewType = "back";
                    }
                    
                    if (foundParent.flipTshirt && typeof foundParent.flipTshirt === 'function') {
                        foundParent._fromPlusIconClick = true;
                        foundParent.flipTshirt(viewType);
                        
                        setTimeout(() => {
                            foundParent.updatePrintAreas(-1);
                            setTimeout(() => {
                                foundParent._fromPlusIconClick = false;
                            }, 100);
                            
                            if (foundParent.$refs && foundParent.$refs.ItemDetails) {
                                if (typeof foundParent.$refs.ItemDetails.calculatePricing === 'function') {
                                    const activeGenderKey = foundParent.active_gender_key || 0;
                                    foundParent.$refs.ItemDetails.calculatePricing(activeGenderKey);
                                }
                            }
                        }, 100);
                    } else {
                        foundParent.updatePrintAreas(matchingLocationIndex);
                        if (foundParent.$refs && foundParent.$refs.ItemDetails) {
                            if (typeof foundParent.$refs.ItemDetails.calculatePricing === 'function') {
                                const activeGenderKey = foundParent.active_gender_key || 0;
                                foundParent.$refs.ItemDetails.calculatePricing(activeGenderKey);
                            }
                        }
                    }
                } else {
                    // Deselecting an area
                    // Determine the viewType of the area being deselected
                    let deselectedViewType = null;
                    if (print_area && print_area.label) {
                        let areaLabel = print_area.label.toLowerCase().trim();
                        if (areaLabel.includes("left")) {
                            deselectedViewType = "left";
                        } else if (areaLabel.includes("right")) {
                            deselectedViewType = "right";
                        } else if (areaLabel.includes("front")) {
                            deselectedViewType = "front";
                        } else if (areaLabel.includes("back")) {
                            deselectedViewType = "back";
                        }
                    }
                    
                    // Check if current view is the one being deselected
                    const currentView = foundParent.active_flip ? foundParent.active_flip.toLowerCase().trim() : null;
                    const isCurrentViewBeingDeselected = deselectedViewType && currentView === deselectedViewType;
                    
                    if (isCurrentViewBeingDeselected) {
                        // Current view is being deselected, find another selected area to switch to
                        let firstSelectedArea = null;
                        let firstSelectedViewType = null;
                        
                        // Find the first selected area (priority: front -> back -> left -> right)
                        // But only check areas that are actually selected
                        const priorityOrder = ["front", "back", "left", "right"];
                        for (const priorityView of priorityOrder) {
                            const selectedLocation = genderGroup.imprint_locations.find((loc) => {
                                if (!loc || !loc.is_selected) return false;
                                const locLabel = (loc.location || "").toLowerCase().trim();
                                if (priorityView === "left" && locLabel.includes("left")) return true;
                                if (priorityView === "right" && locLabel.includes("right")) return true;
                                if (priorityView === "front" && locLabel.includes("front")) return true;
                                if (priorityView === "back" && locLabel.includes("back")) return true;
                                return false;
                            });
                            
                            if (selectedLocation) {
                                firstSelectedArea = selectedLocation;
                                firstSelectedViewType = priorityView;
                                break;
                            }
                        }
                        
                        // If found another selected area, switch to it
                        if (firstSelectedViewType && foundParent.flipTshirt && typeof foundParent.flipTshirt === 'function') {
                            foundParent._fromPlusIconClick = true;
                            foundParent.flipTshirt(firstSelectedViewType);
                            
                            setTimeout(() => {
                                foundParent.updatePrintAreas(-1);
                                setTimeout(() => {
                                    foundParent._fromPlusIconClick = false;
                                }, 100);
                                
                                if (foundParent.$refs && foundParent.$refs.ItemDetails) {
                                    if (typeof foundParent.$refs.ItemDetails.calculatePricing === 'function') {
                                        const activeGenderKey = foundParent.active_gender_key || 0;
                                        foundParent.$refs.ItemDetails.calculatePricing(activeGenderKey);
                                    }
                                }
                            }, 100);
                        } else {
                            // No other selected area found, just update without changing view
                            foundParent._fromPlusIconClick = true;
                            foundParent.updatePrintAreas(-1);
                            setTimeout(() => {
                                foundParent._fromPlusIconClick = false;
                            }, 100);
                            
                            if (foundParent.$refs && foundParent.$refs.ItemDetails) {
                                if (typeof foundParent.$refs.ItemDetails.calculatePricing === 'function') {
                                    const activeGenderKey = foundParent.active_gender_key || 0;
                                    foundParent.$refs.ItemDetails.calculatePricing(activeGenderKey);
                                }
                            }
                        }
                    } else {
                        // Current view is not being deselected, just update without changing view
                        // Set flag to prevent any automatic view changes
                        foundParent._fromPlusIconClick = true;
                        foundParent.updatePrintAreas(-1);
                        setTimeout(() => {
                            foundParent._fromPlusIconClick = false;
                        }, 100);
                        
                        if (foundParent.$refs && foundParent.$refs.ItemDetails) {
                            if (typeof foundParent.$refs.ItemDetails.calculatePricing === 'function') {
                                const activeGenderKey = foundParent.active_gender_key || 0;
                                foundParent.$refs.ItemDetails.calculatePricing(activeGenderKey);
                            }
                        }
                    }
                }
            }

            // Emit event to parent component (in case parent wants to listen)
            this.$emit("togglePrintAreaSelection", {
                area: print_area,
                index: matchingLocationIndex,
                selected: newSelectionState,
                imprintLocation: imprintLocation
            });

            // Force update to reflect changes
            this.$forceUpdate();
        },
        handleImageError(event, print_area) {
            // If short_thumbnail_image fails, try thumbnail as fallback
            if (
                print_area.thumbnail &&
                event.target.src !== print_area.thumbnail
            ) {
                event.target.src = print_area.thumbnail;
            } else {
                // Hide image if both fail
                event.target.style.display = "none";
                console.warn(
                    "Image failed to load for:",
                    print_area.label,
                    print_area
                );
            }
        }
    }
};
</script>
<style scoped>
.print_direction_main {
    display: flex;
    align-items: flex-end;
    gap: 0;
    border-bottom: none;
}

.print-area-tab {
    position: relative;
    display: flex;
    align-items: center;
    background-color: #fff;
    border: 1px solid #d0d0d0;
    border-bottom: none;
    padding: 0;
    transition: all 0.2s ease;
    height: 32px;
}
/* .print-area-tab:nth-child(2), .print-area-tab:nth-child(3){
    border-right: none;
} */

.print-area-tab.active {
    background-color: #0079c1 !important;
    border-color: #0079c1 !important;
    /* z-index: 2; */
}
.print-area-tab.active .print-area-tab-button {
    background-color: #0079c1 !important;
    border-color: #0079c1 !important;
    /* z-index: 2; */
}
.print-area-tab.active img{
    filter: invert(1);
}
.print-area-tab.active::after {
    display: none;
}

.print-area-tab-button {
    display: flex;
    align-items: center;
    gap: 8px;
    padding: 10px 8px 10px 12px;
    background: #fff;
    border: none;
    cursor: pointer;
    outline: none;
    flex: 1;
    border: none !important;
    width: 100%;
    height: 31px;
}

.print-area-tab.active .print-area-tab-button {
    color: #fff;
}

.print-area-tab:not(.active) .print-area-tab-button {
    color: #000;
}

.print-area-icon {
    width: 20px;
    height: 20px;
    object-fit: contain;
    flex-shrink: 0;
}

.print-area-label {
    font-size: 14px;
    font-weight: 500;
    text-transform: uppercase;
    letter-spacing: 0.02em;
    white-space: nowrap;
    margin: 0;
    line-height: 1;
}

.print-area-tab.active .print-area-label {
    color: #fff;
}

.print-area-tab:not(.active) .print-area-label {
    color: #000;
}

.print-area-add-btn {
    width: 16px;
    height: 16px;
    border-radius: 50%;
    border: 1px solid;
    background: transparent !important;
    display: flex;
    align-items: center;
    justify-content: center;
    cursor: pointer;
    padding: 0;
    margin-right: 8px;
    transition: all 0.2s ease;
    outline: none;
    flex-shrink: 0;
}

.print-area-tab.active .print-area-add-btn {
    border-color: #fff;
    color: #fff;
}

.print-area-tab:not(.active) .print-area-add-btn {
    border-color: #000;
    color: #929292;
}

.print-area-add-btn:hover {
    opacity: 0.8;
}

.add-icon {
    width: 14px;
    height: 14px;
    stroke: #929292;
    stroke-width: 2;
    stroke-linecap: round;
}

.tick-icon {
    width: 16px;
    height: 16px;
    display: block;
}

.print-area-add-btn.active-area {
    border: none !important;
    background: transparent !important;
    padding: 0;
}

.print-area-tab.active .print-area-add-btn.active-area {
    border: none !important;
    background: transparent !important;
}

.print-area-tab:not(.active) .print-area-add-btn.active-area {
    border: none !important;
    background: transparent !important;
}

.print-area-all-btn {
    padding: 6px 12px;
    min-width: auto;
    font-weight: 600;
}
.print-area-all-btn .print-area-label {
    margin: 0;
}

.print_direction_block p {
    margin-top: 40px;
    padding-top: 0px;
    position: relative;
    color: #000000;
    font-size: 14px;
    line-height: 14px;
    font-weight: 400;
    text-align: center;
}
</style>